<script setup>
import BlockBackground from '@zyro-inc/site-modules/components/blocks/BlockBackground.vue';
import ModalLayout from '@zyro-inc/site-modules/components/ecommerce/modals/partials/ModalLayout.vue';
import BlockEcommerceProductProviderUser from '@zyro-inc/site-modules/components/blocks/BlockEcommerceProductProviderUser.vue';
import { computed } from 'vue';
import { useEcommerceModal } from '@zyro-inc/site-modules/components/ecommerce/modals/useEcommerceModal';

import {
	getFullWidthSrcset,
	getOptimizedSrc,
} from '@zyro-inc/site-modules/utils/getSrcsets';
import { FULL_WIDTH } from '@zyro-inc/site-modules/utils/getGridItemSize';

import { useEcommerceGlobal } from '@zyro-inc/site-modules/use/useEcommerceGlobal';
import { useSiteGlobal } from '@zyro-inc/site-modules/use/useSiteGlobal';

const props = defineProps({
	translations: {
		type: Object,
		default: () => {},
	},
	isQuickPreview: {
		type: Boolean,
		default: false,
	},
	currentLocale: {
		type: String,
		required: true,
	},
	isCartVisible: {
		type: Boolean,
		default: false,
	},
});

const {
	productPreviewData,
	quickPreviewProductId,
} = useEcommerceModal(props);

const { siteId } = useSiteGlobal();
const { legacyProductPages } = useEcommerceGlobal();

const backgroundSrc = computed(() => getOptimizedSrc(
	productPreviewData.value.background.origin,
	productPreviewData.value.background.path,
	siteId.value,
	{
		width: FULL_WIDTH,
	},
));
const backgroundSrcSet = computed(
	() => getFullWidthSrcset(
		productPreviewData.value.background.origin,
		productPreviewData.value.background.path,
		siteId.value,
		{
			isMobileFullScreen: false,
		},
	),
);
</script>

<template>
	<ModalLayout
		ref="modal"
		max-width="905px"
		width="100%"
		padding="0"
		height="auto"
		overflow="auto"
		class="ecommerce-product-preview"
	>
		<div
			v-qa="'productpreview-modal'"
			class="ecommerce-product-preview__content-wrapper"
		>
			<BlockBackground
				v-if="productPreviewData.background"
				:overlay-opacity="productPreviewData.background['overlay-opacity']"
				:type="productPreviewData.background.current"
				:color="productPreviewData.background.color"
				:gradient="productPreviewData.background.gradient"
				:src="backgroundSrc"
				:srcset="backgroundSrcSet"
				:is-fixed="productPreviewData.attachment === 'fixed'"
				:alt="productPreviewData.background && productPreviewData.background.alt"
				:video-poster="productPreviewData.background.video?.videoThumbnailSrc"
				class="ecommerce-product-preview__background"
			/>
			<BlockEcommerceProductProviderUser
				class="ecommerce-product-preview__content"
				:is-quick-preview="isQuickPreview"
				:data="productPreviewData"
				:quick-preview-product-id="quickPreviewProductId"
				:legacy-product-pages="legacyProductPages"
				:is-cart-visible="isCartVisible"
				:ecommerce-translations="translations"
				:hide-related-products="true"
			/>
		</div>
	</ModalLayout>
</template>

<style lang="scss" scoped>
@import "@zyro-inc/site-modules/scss/mixins/site-engine-mobile";

.ecommerce-product-preview {
	z-index: $z-index-site-engine-ecommerce-preview;

	&__content-wrapper {
		position: relative;
		height: 100%;
		max-height: calc(100vh - 48px);
	}

	&__content {
		position: relative;
		width: 100%;
		height: 100%;
		overflow-y: auto;

		:deep(.block-product), :deep(.skeleton-loader) {
			padding: 32px;
		}
	}

	&__background {
		position: absolute;
		inset: 0;
		z-index: -1;
	}
}

@include site-engine-mobile {
	.ecommerce-product-preview {
		&__content-wrapper {
			max-height: unset;
			min-height: 100vh;
		}

		&__content {
			height: auto;
			min-height: 100vh;
			overflow-y: visible;
		}

		&__background {
			position: fixed;
			inset: 0;
		}
	}
}
</style>
